import { MutationFunctionOptions, MutationTuple } from '@apollo/client';
import { generateUUID } from '@thoughtspot/js-util';
import _ from 'lodash';
import React from 'react';
import { ResourcePool } from '../pooled-mutation/resource-pool';

/** Any mutation wrapped in this hook will wait to execute until all the mutations
 * associated to `isAssociatedObjectMutationInFlightHook` aren't completed
 *
 * @param mutationHookResult original mutationTuple which needs to be wrapped
 * @param isAssociatedObjectMutationInFlightHook pass a hook which returns a
 * boolean represnting if associated object has any mutation in flight
 * @param preMutationFn optional param to invoke any function before firing the mutation to network.
 * @returns Mutation tuple [mutateFn, mutationState]
 */
export const useWaitUntilNoMutationIsInFlightHook = <TData, TVariables>(
    mutationHookResult: MutationTuple<TData, TVariables>,
    isAssociatedObjectMutationInFlightHook: any,
    preMutationFn?: () => void,
): [
    MutationTuple<TData, TVariables>[0],
    MutationTuple<TData, TVariables>[1],
] => {
    const [mutate, state] = mutationHookResult;
    const [guid, setGuid] = React.useState({ id: null });
    const [resourcePool] = React.useState<ResourcePool<{ id: string }>>(
        new ResourcePool(0, 1, () => {
            return { id: generateUUID() };
        }),
    );

    // FIX: Use ref to capture current guid value for cleanup
    // Previous issue: empty dependency array caused cleanup to close over initial null value
    const guidRef = React.useRef(guid);

    React.useEffect(() => {
        guidRef.current = guid;
    }, [guid]);

    React.useEffect(() => {
        return () => {
            // FIX: Use guidRef.current to get the actual current value at cleanup time
            if (guidRef.current?.id) {
                resourcePool.evictResource(guidRef.current);
            }
        };
    }, [resourcePool]);

    const isMutationInProgress = isAssociatedObjectMutationInFlightHook();

    const retrieve = async () => {
        const res = await resourcePool.retrieve();
        setGuid(res);
    };

    React.useEffect(() => {
        if (isMutationInProgress) {
            retrieve();
        } else if (guid?.id) {
            // FIX: Use returnResource instead of evictResource
            // returnResource returns the resource to the pool for reuse
            // evictResource permanently removes it from the pool
            resourcePool.returnResource(guid);
            setGuid({ id: null });
        }
    }, [guid, isMutationInProgress]);

    const pooledMutation = async (
        opts?: MutationFunctionOptions<TData, TVariables>,
    ) => {
        if (preMutationFn) {
            preMutationFn();
        }
        const res = await resourcePool.retrieve();

        // FIX: Use try/finally with returnResource instead of evictResource
        // This ensures resource is returned even on error, allowing reuse
        try {
            return await mutate(opts);
        } finally {
            resourcePool.returnResource(res);
        }
    };

    return [pooledMutation, state];
};
